import { useEffect, useState } from "react";
import { segmentSentences } from "./evidence";
import { formatCaseCitation, formatDecisionDate } from "./local-provider";
import { EvidenceKindChip } from "./results";
import type {
  CaseTextDocument,
  EvidenceSentence,
  SearchProvider,
  SearchResult,
} from "./types";

/**
 * 전문 본문. 카드에서 넘어온 근거 문장 인덱스가 있으면 문장 단위로 나누고
 * 해당 문장만 하이라이트한다. 없으면 기존처럼 pre-wrap 평문이다.
 */
export function CaseTextBody({
  text,
  evidenceSentences,
  className,
}: {
  text: string;
  evidenceSentences?: EvidenceSentence[];
  className: string;
}) {
  const highlighted = new Map<number, EvidenceSentence["kind"]>();
  for (const sentence of evidenceSentences ?? []) {
    highlighted.set(sentence.index, sentence.kind);
  }
  if (highlighted.size === 0) {
    return <div className={className}>{text}</div>;
  }

  return (
    <div className={`${className} case-text-segmented`}>
      {segmentSentences(text).map((sentence) => {
        const kind = highlighted.get(sentence.index);
        return (
          <p
            key={sentence.index}
            className={
              kind
                ? `case-text-sentence is-highlight is-${kind}`
                : "case-text-sentence"
            }
          >
            {sentence.text}
          </p>
        );
      })}
    </div>
  );
}

export interface CaseReaderProps {
  result: SearchResult;
  provider?: SearchProvider;
  onClose: () => void;
  /** 전문 볼륨이 없을 때 SetupPanel을 여는 콜백. */
  onOpenSetup?: () => void;
}

type ReaderState =
  | { phase: "loading" }
  | { phase: "ready"; document: CaseTextDocument }
  | { phase: "missing" }
  | { phase: "error" };

/**
 * 판례 전문 리더. 사이드 패널 안을 가득 채우는 오버레이다.
 *
 * dossier에는 본문을 넣지 않는다. 이 컴포넌트가 열릴 때 provider.caseText()
 * (워커의 PK point lookup + provider LRU)로 한 건만 가져온다.
 */
export function CaseReader({ result, provider, onClose, onOpenSetup }: CaseReaderProps) {
  const [state, setState] = useState<ReaderState>({ phase: "loading" });
  const [copyState, setCopyState] = useState<"idle" | "done" | "fail">("idle");

  const citation =
    result.citation ??
    formatCaseCitation({
      pointer: result.pointer,
      court: result.court,
      caseNo: result.caseNo,
      decisionDate: result.decisionDate,
    });

  const meta = [
    result.court,
    formatDecisionDate(result.decisionDate),
    result.caseNo,
  ]
    .filter(Boolean)
    .join(" · ");

  useEffect(() => {
    let cancelled = false;
    setState({ phase: "loading" });

    if (!provider?.caseText) {
      setState({ phase: "missing" });
      return () => {
        cancelled = true;
      };
    }

    void provider
      .caseText(result.pointer)
      .then((document) => {
        if (cancelled) return;
        if (document.status === "available" && document.text !== undefined) {
          setState({ phase: "ready", document });
        } else if (document.status === "unavailable") {
          setState({ phase: "error" });
        } else {
          setState({ phase: "missing" });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ phase: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [provider, result.pointer]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copy = () => {
    void navigator.clipboard
      .writeText(citation)
      .then(() => {
        setCopyState("done");
        setTimeout(() => setCopyState("idle"), 1400);
      })
      .catch(() => {
        setCopyState("fail");
        setTimeout(() => setCopyState("idle"), 1800);
      });
  };

  return (
    <div className="case-reader" role="dialog" aria-modal="true" aria-label="판례 전문">
      <header className="case-reader-head">
        <button type="button" className="case-reader-back" onClick={onClose} aria-label="닫기">
          ←
        </button>
        <span className="case-reader-meta">{meta || result.label || result.pointer}</span>
        <button type="button" className="case-reader-copy" onClick={copy}>
          {copyState === "done" ? "복사됨" : copyState === "fail" ? "복사 실패" : "인용 복사"}
        </button>
      </header>

      <div className="case-reader-scroll">
        <div className="case-reader-content">
          {state.phase === "loading" ? (
            <p className="case-reader-note">판례 전문을 불러오는 중…</p>
          ) : null}

          {state.phase === "missing" ? (
            <div className="case-reader-missing">
              <p className="case-reader-missing-title">판례 전문 볼륨이 설치되지 않았습니다</p>
              <p className="case-reader-missing-detail">
                판례 본문 볼륨을 설치하면 이 판례의 전문을 볼 수 있습니다.
              </p>
              {onOpenSetup ? (
                <button type="button" className="case-reader-install" onClick={onOpenSetup}>
                  설정에서 설치
                </button>
              ) : null}
            </div>
          ) : null}

          {state.phase === "error" ? (
            <p className="case-reader-note">판례 전문을 불러오지 못했습니다.</p>
          ) : null}

          {state.phase === "ready" ? (
            <>
              {state.document.sections ? (
                <p className="case-reader-count">전문 {state.document.sections}개 섹션</p>
              ) : null}
              {result.evidenceSentences && result.evidenceSentences.length > 0 ? (
                <div className="case-reader-evidence-kinds">
                  {result.evidenceSentences.some(
                    (sentence) => sentence.kind === "citation",
                  ) ? (
                    <EvidenceKindChip kind="citation" />
                  ) : null}
                  {result.evidenceSentences.some(
                    (sentence) => sentence.kind === "related",
                  ) ? (
                    <EvidenceKindChip kind="related" />
                  ) : null}
                </div>
              ) : null}
              <CaseTextBody
                text={state.document.text ?? ""}
                evidenceSentences={result.evidenceSentences}
                className="case-reader-text"
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
